/**
 * Notification settings component
 * Configure notification preferences
 */

import { Card, Form, Switch, Select, Input, Button, Space, Typography, Divider, Alert } from 'antd';
import { SaveOutlined, BellOutlined, MailOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

interface NotificationSettingsProps {
  settings?: NotificationSettingsType;
  loading?: boolean;
  onSave?: (settings: NotificationSettingsType) => void;
}

interface NotificationSettingsType {
  browserNotifications: boolean;
  soundEnabled: boolean;
  emailNotifications: boolean;
  emailAddress?: string;
  notifyOnJobComplete: boolean;
  notifyOnJobFailed: boolean;
  notifyOnSystemError: boolean;
  webhookEnabled: boolean;
  webhookUrl?: string;
}

const DEFAULT_SETTINGS: NotificationSettingsType = {
  browserNotifications: true,
  soundEnabled: false,
  emailNotifications: false,
  notifyOnJobComplete: true,
  notifyOnJobFailed: true,
  notifyOnSystemError: true,
  webhookEnabled: false,
};

export function NotificationSettings({
  settings = DEFAULT_SETTINGS,
  loading,
  onSave,
}: NotificationSettingsProps) {
  const [form] = Form.useForm();

  const handleSave = () => {
    form.validateFields().then((values) => {
      onSave?.(values);
    });
  };

  const requestBrowserPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        form.setFieldValue('browserNotifications', true);
      }
    }
  };

  return (
    <Card
      title={
        <Space>
          <BellOutlined />
          Notification Settings
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={loading}
        >
          Save Settings
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={settings}
        disabled={loading}
      >
        {/* In-app notifications */}
        <Title level={5}>In-App Notifications</Title>

        <Form.Item
          name="browserNotifications"
          label="Browser Notifications"
          valuePropName="checked"
          extra="Show desktop notifications when events occur"
        >
          <Switch
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
            onChange={(checked) => {
              if (checked && 'Notification' in window && Notification.permission !== 'granted') {
                requestBrowserPermission();
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name="soundEnabled"
          label="Sound Alerts"
          valuePropName="checked"
          extra="Play a sound when notifications appear"
        >
          <Switch
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
        </Form.Item>

        <Divider />

        {/* Event triggers */}
        <Title level={5}>Notify Me When</Title>

        <Form.Item
          name="notifyOnJobComplete"
          label="Job Completed"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="notifyOnJobFailed"
          label="Job Failed"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="notifyOnSystemError"
          label="System Error"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Divider />

        {/* Email notifications - Coming Soon */}
        <Title level={5}>
          Email Notifications
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>(Coming Soon)</Text>
        </Title>
        <Alert
          type="info"
          message="Email notifications are not yet implemented"
          description="This feature will be available in a future update."
          style={{ marginBottom: 16 }}
        />

        <Divider />

        {/* Webhook notifications - Coming Soon */}
        <Title level={5}>
          Webhook Integration
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>(Coming Soon)</Text>
        </Title>
        <Alert
          type="info"
          message="Webhook notifications are not yet implemented"
          description="Support for Slack, Discord, and custom webhooks will be available in a future update."
          style={{ marginBottom: 16 }}
        />

        {/* Browser permission alert */}
        {'Notification' in window && Notification.permission === 'denied' && (
          <Alert
            type="warning"
            message="Browser notifications are blocked"
            description="You have blocked notifications for this site. Please update your browser settings to enable desktop notifications."
            style={{ marginTop: 16 }}
          />
        )}
      </Form>
    </Card>
  );
}

export default NotificationSettings;
